import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { Users, Plus, Search, Edit, Trash2, Phone, Mail, Download, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CLASS = {
    active: "badge-active", inactive: "badge-inactive",
    debtor: "badge-debtor", pending: "badge-pending"
};

const defaultForm = { type: "business", name: "", company: "", nipt: "", phone: "", email: "", address: "", status: "active", notes: "" };

export default function ClientsPage() {
    const { t } = useLang();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const canEdit = ["admin", "staff"].includes(user?.role);
    const canDelete = user?.role === "admin";

    const fetchClients = async () => {
        setLoading(true);
        try {
            const params = { limit: 100, skip: 0 };
            if (search) params.q = search;
            if (statusFilter && statusFilter !== "all") params.status = statusFilter;
            const { data } = await axios.get(`${API}/clients`, { params, withCredentials: true });
            setClients(data.items);
            setTotal(data.total);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => {
        const timer = setTimeout(fetchClients, 300);
        return () => clearTimeout(timer);
    }, [search, statusFilter]);

    const openAdd = () => { setEditItem(null); setForm(defaultForm); setError(""); setShowModal(true); };
    const openEdit = (item) => { setEditItem(item); setForm({ ...item }); setError(""); setShowModal(true); };

    const handleSubmit = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            if (editItem) {
                await axios.put(`${API}/clients/${editItem.id}`, form, { withCredentials: true });
            } else {
                await axios.post(`${API}/clients`, form, { withCredentials: true });
            }
            setShowModal(false); fetchClients();
        } catch (e) {
            setError(e.response?.data?.detail || t("error"));
        } finally { setSaving(false); }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(t("confirm_delete"))) return;
        try {
            await axios.delete(`${API}/clients/${item.id}`, { withCredentials: true });
            fetchClients();
        } catch (e) { alert(e.response?.data?.detail || t("error")); }
    };

    const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const handleExport = async () => {
        try {
            const res = await axios.get(`${API}/export/clients.csv`, { withCredentials: true, responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `klientet-${new Date().toISOString().substring(0, 10)}.csv`);
            document.body.appendChild(link); link.click(); link.remove();
        } catch { alert("Gabim gjatë eksportimit"); }
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <Users size={20} className="text-[#E7212E]" /> {t("clients")}
                    </h1>
                    <p className="text-sm text-slate-500 mt-0.5">{total} {t("clients").toLowerCase()} gjithsej</p>
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        <button onClick={handleExport} data-testid="export-clients-btn"
                            className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium px-3 py-2 rounded transition-colors">
                            <Download size={14} /> CSV
                        </button>
                        <button onClick={openAdd} data-testid="add-client-btn"
                            className="flex items-center gap-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                            <Plus size={15} /> {t("add")}
                        </button>
                    </div>
                )}
            </div>

            <div className="flex gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                    <input
                        placeholder={`${t("search")} emër, NIPT, telefon...`}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        data-testid="client-search"
                        className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-44" data-testid="client-status-filter">
                        <SelectValue placeholder={t("filter")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t("all_statuses")}</SelectItem>
                        <SelectItem value="active">{t("active")}</SelectItem>
                        <SelectItem value="inactive">{t("inactive")}</SelectItem>
                        <SelectItem value="debtor">{t("debtor")}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-slate-200 rounded overflow-hidden">
                <table className="w-full crm-table" data-testid="clients-table">
                    <thead>
                        <tr>
                            <th>{t("client_name")}</th>
                            <th>NIPT</th>
                            <th>{t("phone")}</th>
                            <th>{t("address")}</th>
                            <th>{t("status")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-10 text-slate-400">{t("loading")}</td></tr>
                        ) : clients.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-10 text-slate-400">{t("no_data")}</td></tr>
                        ) : clients.map(c => (
                            <tr key={c.id} data-testid={`client-row-${c.id}`}
                                className="cursor-pointer hover:bg-slate-50"
                                onClick={() => navigate(`/clients/${c.id}`)}>
                                <td>
                                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                                        {c.name}
                                        <ExternalLink size={11} className="text-slate-300" />
                                    </div>
                                    {c.company && <div className="text-xs text-slate-500">{c.company}</div>}
                                    <div className="text-xs text-slate-400 capitalize">{c.type === "business" ? t("business") : t("individual")}</div>
                                </td>
                                <td className="font-mono text-sm">{c.nipt || "—"}</td>
                                <td>
                                    <div className="flex items-center gap-1 text-sm">
                                        <Phone size={12} className="text-slate-400" /> {c.phone}
                                    </div>
                                    {c.email && <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                                        <Mail size={11} className="text-slate-400" /> {c.email}
                                    </div>}
                                </td>
                                <td className="text-sm text-slate-600 max-w-xs truncate">{c.address || "—"}</td>
                                <td>
                                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${STATUS_CLASS[c.status] || STATUS_CLASS.inactive}`}>
                                        {t(c.status)}
                                    </span>
                                </td>
                                <td onClick={e => e.stopPropagation()}>
                                    <div className="flex items-center gap-1">
                                        {canEdit && (
                                            <button onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}
                                                className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors">
                                                <Edit size={14} />
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button onClick={() => handleDelete(c)} data-testid={`delete-client-${c.id}`}
                                                className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors">
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editItem ? "Modifiko Klientin" : "Shto Klient të Ri"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-3 mt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Lloji</label>
                                <Select value={form.type} onValueChange={v => f("type", v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="business">{t("business")}</SelectItem>
                                        <SelectItem value="individual">{t("individual")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("status")}</label>
                                <Select value={form.status} onValueChange={v => f("status", v)}>
                                    <SelectTrigger data-testid="client-status-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">{t("active")}</SelectItem>
                                        <SelectItem value="inactive">{t("inactive")}</SelectItem>
                                        <SelectItem value="debtor">{t("debtor")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Emri *</label>
                                <input required value={form.name} onChange={e => f("name", e.target.value)}
                                    data-testid="client-name-input"
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Kompania</label>
                                <input value={form.company || ""} onChange={e => f("company", e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">NIPT</label>
                                <input value={form.nipt || ""} onChange={e => f("nipt", e.target.value)}
                                    data-testid="client-nipt-input" placeholder="L12345678A"
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Telefoni *</label>
                                <input required value={form.phone || ""} onChange={e => f("phone", e.target.value)}
                                    data-testid="client-phone-input"
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Email</label>
                                <input type="email" value={form.email || ""} onChange={e => f("email", e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Adresa</label>
                                <input value={form.address || ""} onChange={e => f("address", e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                        </div>
                        {error && <p className="text-rose-600 text-sm bg-rose-50 p-2 rounded">{error}</p>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowModal(false)}
                                className="px-4 py-2 border border-slate-200 rounded text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                                {t("cancel")}
                            </button>
                            <button type="submit" disabled={saving} data-testid="save-client-btn"
                                className="px-4 py-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium rounded transition-colors disabled:opacity-60">
                                {saving ? "Duke ruajtur..." : t("save")}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
